import { api } from "@/redux/api";

import {
	ArchivedVideoWizard,
	ArchivedVideoWizardListSchema,
	VideoWizard,
	VideoWizardSchema,
	WizardAudioConfig,
	WizardExportConfig,
} from "./videoWizard.schema";

export interface UpdateVideoWizardPayload {
	id: number;
	/** The version last read; the server rejects a stale write rather than clobbering it. */
	version: number;
	prompt?: string;
	targetLengthSeconds?: number;
	styleId?: string;
	/**
	 * Chosen at step 1. Changing it resets the storyboard and everything under it, since
	 * the scenes are written in it — so this carries `force` like the generate calls.
	 */
	narrationLanguage?: string;
	/**
	 * Merged field by field on the server, so a patch may carry one setting. Changing any
	 * of them after step 3 is approved resets what the narration was built on, which is
	 * why this payload carries `force` like the generate calls do.
	 */
	audioConfig?: WizardAudioConfig;
	/**
	 * Merged field by field like `audioConfig`. Changing the transition or the format list
	 * after a successful export resets step 5, which is why this payload carries `force`.
	 */
	exportConfig?: WizardExportConfig;
	stepModels?: Record<string, number>;
	force?: boolean;
}

export interface GenerateStoryboardPayload {
	id: number;
	feedback?: string;
	force?: boolean;
}

/**
 * No `sceneIds` regenerates every scene — the mockup's "Alle Bilder neu generieren"; one
 * id is the per-card iteration, and its feedback is kept as that scene's own.
 */
export interface GenerateSceneImagesPayload {
	id: number;
	sceneIds?: number[];
	feedback?: string;
	force?: boolean;
}

/**
 * No `sceneIds` renders every scene — the mockup's "Alle N Szenen generieren"; one id is
 * the per-card regenerate, and its feedback is kept as that scene's own.
 */
export interface GenerateSceneVideosPayload {
	id: number;
	sceneIds?: number[];
	feedback?: string;
	force?: boolean;
}

/**
 * The whole cut, written in one call — clip order and trim points together.
 *
 * Whole rather than per-clip because reordering is a property of the sequence rather than
 * of any one clip. The server renumbers the positions from the order it receives, so the
 * client only has to get the sequence right.
 */
export interface SaveTimelinePayload {
	id: number;
	version: number;
	clips: {
		storyboardSceneId: number;
		position: number;
		inSeconds: number;
		outSeconds: number;
		/** The narration's own line. Omitted means untouched, which is what the server assumes. */
		narrationOffsetSeconds?: number | null;
		narrationInSeconds?: number | null;
		narrationOutSeconds?: number | null;
	}[];
	force?: boolean;
}

export interface SceneMutationPayload {
	id: number;
	sceneId: number;
}

interface MutationLifecycle {
	dispatch: (action: unknown) => void;
	queryFulfilled: Promise<{ data: VideoWizard }>;
}

/**
 * Every mutation answers with the whole aggregate, so each one writes the server's version
 * of the truth straight into the cache rather than re-deriving anything locally. The
 * response's own id is used, so this works for `start` — which is called with a course id
 * — exactly as it does for the rest.
 *
 * A rejected mutation leaves the cache alone: what is on screen stays the last state the
 * server confirmed, which is what makes the 409 confirm-and-retry flow safe.
 */
const syncWizardCache = async (_arg: unknown, { dispatch, queryFulfilled }: MutationLifecycle) => {
	try {
		const { data } = await queryFulfilled;

		dispatch(videoWizardApi.util.upsertQueryData("getVideoWizard", data.id, data));
	} catch {
		/* keep the confirmed state */
	}
};

export const videoWizardApi = api.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * Starts the wizard, or hands back the one already open. Safe to call more than
		 * once — the server decides whether that means "create" or "resume".
		 */
		startVideoWizard: builder.mutation<VideoWizard, number>({
			query: (courseId) => ({
				url: `course/${courseId}/video-wizard`,
				method: "POST",
				responseSchema: VideoWizardSchema,
			}),
			invalidatesTags: ["VideoWizard"],
			onQueryStarted: syncWizardCache,
		}),

		/** 204 when the scenario has never started one — RTK Query gives `null` for that. */
		getCourseVideoWizard: builder.query<VideoWizard | null, number>({
			query: (courseId) => ({
				url: `course/${courseId}/video-wizard`,
				responseSchema: VideoWizardSchema.nullable(),
			}),
			transformResponse: (response: VideoWizard | null) => response ?? null,
			providesTags: ["VideoWizard"],
		}),

		/** The scenario's archived sessions, newest first — the editor tab lists them (BCM360-375). */
		getArchivedVideoWizards: builder.query<ArchivedVideoWizard[], number>({
			query: (courseId) => ({
				url: `course/${courseId}/video-wizard/archived`,
				responseSchema: ArchivedVideoWizardListSchema,
			}),
			providesTags: ["VideoWizard"],
		}),

		getVideoWizard: builder.query<VideoWizard, number>({
			query: (id) => ({
				url: `media/video-wizard/${id}`,
				responseSchema: VideoWizardSchema,
			}),
			providesTags: (_result, _error, id) => [{ type: "VideoWizard" as const, id }],
		}),

		updateVideoWizard: builder.mutation<VideoWizard, UpdateVideoWizardPayload>({
			query: ({ id, ...body }) => ({
				url: `media/video-wizard/${id}`,
				method: "PATCH",
				body,
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		/**
		 * Turns a picture already in the scenario's library into a graphical style. 202: the
		 * analysed description lands over the socket like any other job, and the style is in
		 * the aggregate's `styles` from this response on — ANALYSING until then.
		 */
		createWizardStyle: builder.mutation<
			VideoWizard,
			{ id: number; courseFileId: number; force?: boolean }
		>({
			query: ({ id, ...body }) => ({
				url: `media/video-wizard/${id}/styles`,
				method: "POST",
				body,
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		/**
		 * The author's own wording for an uploaded style. The server stales exactly that
		 * style's images in reply — the key still names them, the look no longer does — so
		 * the aggregate this returns is what says which scenes need rendering again.
		 */
		updateWizardStyle: builder.mutation<
			VideoWizard,
			{ id: number; styleRowId: number; promptFragment: string; force?: boolean }
		>({
			query: ({ id, styleRowId, ...body }) => ({
				url: `media/video-wizard/${id}/styles/${styleRowId}`,
				method: "PATCH",
				body,
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		generateStoryboard: builder.mutation<VideoWizard, GenerateStoryboardPayload>({
			query: ({ id, ...body }) => ({
				url: `media/video-wizard/${id}/storyboard/generate`,
				method: "POST",
				body,
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		activateStoryboardVersion: builder.mutation<
			VideoWizard,
			{ id: number; versionId: number; force?: boolean }
		>({
			query: ({ id, versionId, force }) => ({
				url: `media/video-wizard/${id}/storyboard/versions/${versionId}/activate`,
				method: "POST",
				body: { force },
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		addStoryboardScene: builder.mutation<
			VideoWizard,
			{
				id: number;
				title?: string;
				description?: string;
				durationSeconds?: number;
				force?: boolean;
			}
		>({
			query: ({ id, ...body }) => ({
				url: `media/video-wizard/${id}/scenes`,
				method: "POST",
				body,
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		/** An in-place edit; deliberately does not reset anything downstream. */
		updateStoryboardScene: builder.mutation<
			VideoWizard,
			SceneMutationPayload & { title?: string; description?: string; durationSeconds?: number }
		>({
			query: ({ id, sceneId, ...body }) => ({
				url: `media/video-wizard/${id}/scenes/${sceneId}`,
				method: "PATCH",
				body,
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		deleteStoryboardScene: builder.mutation<
			VideoWizard,
			SceneMutationPayload & { force?: boolean }
		>({
			query: ({ id, sceneId, force }) => ({
				url: `media/video-wizard/${id}/scenes/${sceneId}`,
				method: "DELETE",
				body: { force },
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		/**
		 * The scene image as bytes to save — BCM360-330. Through the API rather than the
		 * asset URL: the bucket is another origin with no CORS header, where a link can only
		 * open a tab; the API streams the same bytes with a download disposition and a
		 * readable filename, which arrives in the content-disposition header read here.
		 */
		downloadSceneImage: builder.mutation<
			{ blob: Blob; filename: string },
			{ id: number; sceneId: number }
		>({
			query: ({ id, sceneId }) => ({
				url: `media/video-wizard/${id}/scenes/${sceneId}/image/download`,
				responseHandler: (response) => response.blob(),
			}),
			transformResponse: (blob: Blob, meta) => {
				// The wrapper's meta type is opaque; at runtime this is fetchBaseQuery's meta.
				const response = (meta as { response?: Response } | undefined)?.response;
				const disposition = response?.headers.get("content-disposition") ?? "";
				const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? "szene.png";

				return { blob, filename };
			},
		}),

		/**
		 * A finished export file as bytes to save — a video by format preset, or the
		 * subtitle sidecar. Through the API for the same reason the scene image is: the
		 * bucket is another origin without CORS, where a link can only open a tab.
		 */
		downloadExportFile: builder.mutation<
			{ blob: Blob; filename: string },
			{ id: number; formatPresetId?: number; subtitle?: boolean }
		>({
			query: ({ id, formatPresetId, subtitle }) => ({
				url: `media/video-wizard/${id}/export/download`,
				params: {
					...(formatPresetId ? { formatPresetId } : {}),
					...(subtitle ? { subtitle: true } : {}),
				},
				responseHandler: (response) => response.blob(),
			}),
			transformResponse: (blob: Blob, meta) => {
				const response = (meta as { response?: Response } | undefined)?.response;
				const disposition = response?.headers.get("content-disposition") ?? "";
				const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? "export.mp4";

				return { blob, filename };
			},
		}),

		/** One job per scene on the server, so each card finishes and bills on its own. */
		generateSceneImages: builder.mutation<VideoWizard, GenerateSceneImagesPayload>({
			query: ({ id, ...body }) => ({
				url: `media/video-wizard/${id}/scene-images/generate`,
				method: "POST",
				body,
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		/**
		 * One job per scene on the server, so each card finishes, bills and appears on its
		 * own — and a half-failed batch keeps the scenes it already paid for, which at this
		 * step's prices is the whole point.
		 */
		generateSceneVideos: builder.mutation<VideoWizard, GenerateSceneVideosPayload>({
			query: ({ id, ...body }) => ({
				url: `media/video-wizard/${id}/scene-videos/generate`,
				method: "POST",
				body,
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		/**
		 * The per-scene "✓ Szene freigeben" — the only approval in the wizard that is not a
		 * whole step. A finished render is *"Prüfung offen"* until someone has watched it.
		 */
		approveSceneVideo: builder.mutation<VideoWizard, SceneMutationPayload>({
			query: ({ id, sceneId }) => ({
				url: `media/video-wizard/${id}/scenes/${sceneId}/approve-video`,
				method: "POST",
				body: {},
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		/**
		 * A short sample in the chosen voice, billed at a fraction of a full track. It
		 * approves nothing — the finished sample arrives on the aggregate as
		 * `narrationPreviewUrl`, so a reload replays it rather than paying for it twice.
		 */
		previewNarration: builder.mutation<VideoWizard, { id: number }>({
			query: ({ id }) => ({
				url: `media/video-wizard/${id}/narration/preview`,
				method: "POST",
				body: {},
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		/**
		 * Step 3's generate — writes the narration script and its governance report, with the
		 * author's feedback for the script model. No audio: the voice is step 4's.
		 */
		generateNarrationScript: builder.mutation<
			VideoWizard,
			{ id: number; feedback?: string; brief?: string; sceneIds?: number[]; force?: boolean }
		>({
			query: ({ id, ...body }) => ({
				url: `media/video-wizard/${id}/narration/script`,
				method: "POST",
				body,
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		/**
		 * The author's own words for one scene — checked and reported on, never refused.
		 * Costs the spoken take, which is why it carries `force` like the generate calls.
		 */
		updateSceneScript: builder.mutation<
			VideoWizard,
			{ id: number; sceneId: number; text: string; force?: boolean }
		>({
			query: ({ id, sceneId, ...body }) => ({
				url: `media/video-wizard/${id}/scenes/${sceneId}/narration`,
				method: "PATCH",
				body,
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		/**
		 * Step 4's generate — speaks the script step's approved words in the chosen voice.
		 * Approving the step is a separate, plain approve once the track exists.
		 */
		generateNarration: builder.mutation<VideoWizard, { id: number; force?: boolean }>({
			query: ({ id, ...body }) => ({
				url: `media/video-wizard/${id}/narration/generate`,
				method: "POST",
				body,
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		/**
		 * Saves the author's cut.
		 *
		 * The one call in the wizard that is deliberately *not* fired per interaction. Every
		 * mutation bumps the session's `version`, and the wizard refetches on every socket
		 * push — so a write per drag frame would race itself into `STALE_VERSION`. Step 5
		 * debounces to the end of an interaction and flushes before exporting.
		 */
		saveTimeline: builder.mutation<VideoWizard, SaveTimelinePayload>({
			query: ({ id, ...body }) => ({
				url: `media/video-wizard/${id}/timeline`,
				method: "PUT",
				body,
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		/**
		 * Renders the finished video. One job covers every format the author chose, which is
		 * why the price on the button does not move when they add one.
		 *
		 * Approves nothing by itself: the export job links the video as the scenario's intro,
		 * approves step 5 and completes the session when it succeeds, and that arrives over
		 * the socket.
		 */
		exportVideo: builder.mutation<VideoWizard, { id: number; force?: boolean }>({
			query: ({ id, force }) => ({
				url: `media/video-wizard/${id}/export`,
				method: "POST",
				body: { force },
				responseSchema: VideoWizardSchema,
			}),
			onQueryStarted: syncWizardCache,
		}),

		/**
		 * Step 3 is the exception: approving it queues the narration and answers with the
		 * step still `IN_PROGRESS` and the job in `activeJobs`. The approval itself lands
		 * over the socket when that job succeeds.
		 */
		approveWizardStep: builder.mutation<VideoWizard, { id: number; step: number; force?: boolean }>(
			{
				query: ({ id, step, force }) => ({
					url: `media/video-wizard/${id}/steps/${step}/approve`,
					method: "POST",
					body: { force },
					responseSchema: VideoWizardSchema,
				}),
				onQueryStarted: syncWizardCache,
			}
		),

		/**
		 * Ends a session the author no longer needs (BCM360-375). Nothing is deleted and the
		 * intro video stays on the scenario; the next start is a new, empty session, which is
		 * why every wizard read is invalidated rather than only this session's.
		 */
		archiveVideoWizard: builder.mutation<VideoWizard, { id: number; version: number }>({
			query: ({ id, version }) => ({
				url: `media/video-wizard/${id}/archive`,
				method: "POST",
				body: { version },
				responseSchema: VideoWizardSchema,
			}),
			invalidatesTags: ["VideoWizard"],
		}),
	}),
});

export const {
	useStartVideoWizardMutation,
	useGetCourseVideoWizardQuery,
	useGetVideoWizardQuery,
	useUpdateVideoWizardMutation,
	useCreateWizardStyleMutation,
	useUpdateWizardStyleMutation,
	useGenerateStoryboardMutation,
	useActivateStoryboardVersionMutation,
	useAddStoryboardSceneMutation,
	useUpdateStoryboardSceneMutation,
	useDeleteStoryboardSceneMutation,
	useDownloadExportFileMutation,
	useDownloadSceneImageMutation,
	useGenerateSceneImagesMutation,
	useGenerateSceneVideosMutation,
	useApproveSceneVideoMutation,
	useGenerateNarrationMutation,
	useGenerateNarrationScriptMutation,
	useUpdateSceneScriptMutation,
	usePreviewNarrationMutation,
	useSaveTimelineMutation,
	useExportVideoMutation,
	useApproveWizardStepMutation,
	useArchiveVideoWizardMutation,
	useGetArchivedVideoWizardsQuery,
} = videoWizardApi;
